import { useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Button, { BUTTON_POSITION, BUTTON_TYPE, TOGGLE_TYPE } from '../../components/Button';
import { RepositoryContext } from '../../store/RepositoryProvider';
import { UserContext } from '../../store/UserProvider';
import { OrderContext } from '../../store/OrderProvider';
import API from '../../server/api';
import DayPicker, { DAY_PICKER_MODE } from '../../components/DayPicker';

const getDateFormat = (date) => {
  return date ? `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}` : '';
};

const SelectDate = ({ onSelect, onCancel, onApply }) => {
  const {t} = useTranslation(['common', 'order']);

  const userInfo = useContext(UserContext);
  const orderInfo = useContext(OrderContext);
  const repository = useContext(RepositoryContext);

  const [seats, setSeats] = useState({});
  const [time, setTime] = useState(orderInfo.getSession().time);
  const [schedule, setSchedule] = useState(orderInfo.getSession().schedule);
  const [date, setDate] = useState(new Date(orderInfo.getSession().date));
  const [appointedDate, setAppointedDate] = useState({ time: orderInfo.getSession().time, date: orderInfo.getSession().date, schedule: orderInfo.getSession().schedule });

  const handleSelectDate = (day) => {
    setDate(day);
    if (day && day !== date && seats[getDateFormat(day)] && seats[getDateFormat(day)].times.length > 0) {
      setTime(seats[getDateFormat(day)].times[0].time);
      setSchedule(seats[getDateFormat(day)].times[0]._id);
      setAppointedDate({ time: seats[getDateFormat(day)].times[0].time, date: day, schedule: seats[getDateFormat(day)].times[0]._id });
    }

    if (day) {
      typeof onSelect === 'function' && onSelect(day);
    }
  };


  const formatDay = useCallback((day) => {
    const date = day.getDate();

    return (
      <div className={'calendar-day' + (seats[getDateFormat(day)] ? ' have-seats' : '')}>
        <div className="calendar-date">{date}</div>
        {seats[getDateFormat(day)] && (
          <div className="calendar-seat">
            { seats[getDateFormat(day)].seats || ''}{seats[getDateFormat(day)].stopSelling ? '' : '+'}
          </div>
        )}
      </div>
    );
  }, [seats]);

  const handleApplyDate = useCallback(() => {
    typeof onApply === 'function' && onApply(appointedDate.date, appointedDate.time, appointedDate.schedule);
  }, [time, date, schedule]);

  const handleCancel = useCallback(() => {
    typeof onCancel === 'function' && onCancel();
  }, []);

  const handleTime = useCallback((time, schedule) => {
    setTime(time);
    setSchedule(schedule);
    setAppointedDate({ time, date, schedule });
  }, [time, date]);


  useEffect(() => {
    repository.request(API.schedule.datesSeats, { accountId: userInfo.getSession().account, id: orderInfo.getSession().excursion, subdivision: userInfo.getSession().subdivision }, Object).then((dates) => {
      Object.keys(dates).forEach((date) => {
        if (dates[date]) {
          setSeats((prevState) => {
            prevState[date] = {
              seats: dates[date].reduce((seats, transport) => seats += transport.stopSelling ? transport.seats : 0, 0),
              stopSelling: dates[date].reduce((stopSelling, transport) => stopSelling |= transport.stopSelling, false),
              times: dates[date]
            };
            return prevState;
          });
        } else {
          setSeats({});
        }
      });
    }).catch((e) => {
      setSeats({});
    });
  }, []);

  return (
    <div className="trip-app">
      <div className="trip-app-body">
        <DayPicker
          mode={DAY_PICKER_MODE.SINGLE}
          formatters={{ formatDay }}
          selected={date}
          onSelect={handleSelectDate}
          footer={date && seats[getDateFormat(date)] && (
            <Button.ToggleGroup>
              {seats[getDateFormat(date)].times.map((_time) => (
                <Button.Toggle key={_time.time}  type={TOGGLE_TYPE.RADIO} name={'schedule-time'} checked={appointedDate.time === _time.time} value={_time.time} onChange={(e) => { handleTime(_time.time, _time._id); }}>
                  {_time.time} - {_time.stopSelling ? t('common:place.place', { count: _time.seats }) : t('order:select_date.many_places')}
                </Button.Toggle>
              ))}
            </Button.ToggleGroup>
          )}
        />
      </div>
      <div>
        <Button.Panel>
          <Button variant={BUTTON_TYPE.PRIMARY} position={BUTTON_POSITION.LEFT} onClick={handleApplyDate}>{t('common:button.save')}</Button>
          <Button variant={BUTTON_TYPE.SECONDARY} position={BUTTON_POSITION.RIGHT} onClick={handleCancel}>{t('common:button.cancel')}</Button>
        </Button.Panel>
      </div>
    </div>
  );
}

SelectDate.propTypes = {
  onSelect: PropTypes.func,
  onCancel: PropTypes.func,
  onApply: PropTypes.func,
};

export default SelectDate;